/**
 * SessionLeaderGridCell — MED-906 / E4.0c
 *
 * Single cell of the Session Leaders grid. Two modes:
 *
 *   • `mode="live"` — picker writes directly to the backend via the existing
 *     PUT /api/cohorts/:cohortId/sessions/:sessionId/leader (MED-812). Used
 *     in the empty/finalized phases.
 *
 *   • `mode="proposal"` — picker updates the parent's in-memory proposal map
 *     via `onProposalChange` and never hits the network. Used while the CM
 *     reviews a generated rotation before clicking Finalize.
 *
 * When the session is past `scheduled_start` the cell renders read-only,
 * mirroring the inline cell on the Sessions tab.
 */
import { useMemo, useState } from 'react';
import { Group, Loader, Select, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { sessionsApi } from '../../../../services/api/sessions';
import type {
  SessionLeadersGridGroupMember,
  SessionLeadersGridSession,
} from '../../../../services/api/sessionLeaders';

type CellMode = 'live' | 'proposal';

interface SessionLeaderGridCellProps {
  cohortId: string;
  session: SessionLeadersGridSession;
  groupMembers: SessionLeadersGridGroupMember[];
  /** When mode === 'proposal', overrides session.leader.user_id as the displayed selection. */
  proposedLeaderId?: string | null;
  /** Visual marker for proposal-state cells (italic + tinted background). */
  isProposal?: boolean;
  mode?: CellMode;
  onProposalChange?: (sessionId: string, newLeaderId: string) => void;
  onUpdated: () => void;
}

function formatMemberLabel(m: SessionLeadersGridGroupMember): string {
  return `${m.first_name} ${m.last_name}`.trim() || m.user_id;
}

export function SessionLeaderGridCell({
  cohortId,
  session,
  groupMembers,
  proposedLeaderId,
  isProposal = false,
  mode = 'live',
  onProposalChange,
  onUpdated,
}: SessionLeaderGridCellProps) {
  const [saving, setSaving] = useState(false);

  const scheduledStartTime = session.scheduled_start
    ? new Date(session.scheduled_start).getTime()
    : null;
  const isLocked =
    scheduledStartTime !== null &&
    !Number.isNaN(scheduledStartTime) &&
    Date.now() >= scheduledStartTime;

  // In proposal mode the displayed value comes from the parent's proposal
  // state; in live mode it reflects the persisted session.leader.
  const displayedLeaderId =
    mode === 'proposal'
      ? (proposedLeaderId ?? session.leader?.user_id ?? null)
      : (session.leader?.user_id ?? null);

  const memberById = useMemo(() => {
    const map = new Map<string, SessionLeadersGridGroupMember>();
    for (const m of groupMembers) map.set(m.user_id, m);
    return map;
  }, [groupMembers]);

  const leaderLabel = (() => {
    if (displayedLeaderId) {
      const member = memberById.get(displayedLeaderId);
      if (member) return formatMemberLabel(member);
      if (mode === 'live' && session.leader) {
        return (
          `${session.leader.first_name} ${session.leader.last_name}`.trim() ||
          session.leader.user_id
        );
      }
      return displayedLeaderId;
    }
    return 'Unassigned';
  })();
  const isAssigned = displayedLeaderId !== null;

  const selectData = useMemo(
    () =>
      groupMembers.map(m => ({
        value: m.user_id,
        label: formatMemberLabel(m),
      })),
    [groupMembers],
  );

  const handleChange = async (newLeaderId: string | null) => {
    if (!newLeaderId) return;
    if (newLeaderId === displayedLeaderId) return;

    if (mode === 'proposal') {
      onProposalChange?.(session.session_id, newLeaderId);
      return;
    }

    setSaving(true);
    try {
      await sessionsApi.assignSessionLeader(cohortId, session.session_id, newLeaderId);
      notifications.show({
        color: 'medleyGreen',
        title: 'Leader updated',
        message: `Session ${session.session_number ?? ''} leader set.`,
      });
      onUpdated();
    } catch (err) {
      const apiErr = err as Error & { status?: number; code?: string };
      const code = apiErr.code;
      const friendlyMessage =
        code === 'session_already_started'
          ? 'Session has already started; leader cannot be changed.'
          : code === 'participant_not_in_group'
            ? 'Selected user is no longer a member of this group.'
            : code === 'not_peer_led'
              ? 'This session is not configured for peer leadership.'
              : (apiErr.message ?? 'Failed to update session leader.');
      notifications.show({
        color: 'dangerRed',
        title: 'Could not set leader',
        message: friendlyMessage,
      });
    } finally {
      setSaving(false);
    }
  };

  if (isLocked) {
    return (
      <Text size="sm" fw={isAssigned ? 500 : 400} c={isAssigned ? undefined : 'dimmed'}>
        {leaderLabel}
      </Text>
    );
  }

  const proposalStyle = isProposal
    ? {
        background: 'var(--mantine-color-warningAmber-0, #FEF7E6)',
        borderRadius: 4,
        padding: '2px 4px',
      }
    : undefined;

  return (
    <Group gap={4} wrap="nowrap" style={{ minWidth: 160, ...proposalStyle }}>
      <Select
        size="xs"
        searchable
        data={selectData}
        value={displayedLeaderId}
        placeholder="Unassigned"
        nothingFoundMessage="No members in group"
        aria-label={`Assign leader for session ${session.session_number ?? ''}`}
        disabled={saving || groupMembers.length === 0}
        onChange={handleChange}
        comboboxProps={{ withinPortal: true }}
        style={{
          flex: 1,
          fontStyle: isProposal ? 'italic' : undefined,
        }}
      />
      {saving && <Loader size="xs" />}
    </Group>
  );
}

export default SessionLeaderGridCell;
